import duration from 'dayjs/plugin/duration';
import dayjs from 'dayjs';
// Types
import { GetMediaPost_getMediaPost } from 'api/mediaPost/types/GetMediaPost';
import { GetStreamExtended_stream } from 'api/streams/types/GetStreamExtended';
import { GetStream_stream } from 'api/streams/types/GetStream';
import {
  MemorabiliaStatus,
  StreamStatus,
  TicketEventStatus,
} from 'api/graphql-global-types';
import { PublicTicketEventFields } from 'api/ticketEvents/types/PublicTicketEventFields';
// Helpers
import {
  getEnvLink,
  computeOrderStreamPath,
  computeVideoWatchStreamPath,
} from 'helpers/routes';
import {
  hasStreamRecordsV2,
  getStreamRecordsFromV2,
  getStreamerName,
} from 'helpers/streams';
import { parseForInnerHTML } from 'helpers/textEditor';
import { isTicketEventSelling } from 'helpers/ticketSales';
// Constants
import { DEFAULT_CURRENCY } from 'constants/global';
import { EVENT_TICKETS } from 'constants/routes';

dayjs.extend(duration);

type StreamType = GetStreamExtended_stream | GetStream_stream;

const MILLIONS_ORG = {
  '@type': 'Organization',
  name: 'MILLIONS',
  url: getEnvLink(),
};

const MILLIONS_SOCIAL_PROFILES = [
  'https://www.instagram.com/millionsdotco/',
  'https://twitter.com/millionsdotco',
  'https://www.tiktok.com/@millionsdotco',
  'https://www.youtube.com/channel/UCyIadicC0Ac0R4yU3-cqRgA',
  'https://www.facebook.com/millionsdotco',
  'https://www.linkedin.com/company/millionsdotco/',
];

/**
 * Organization + WebSite schema for the homepage. Organization (with logo
 * and sameAs profiles) feeds Google's knowledge panel and AI answer
 * engines; WebSite names the site for search features.
 */
export const generateHomeStructuredData = (): Record<string, unknown>[] => {
  const url = getEnvLink();

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${url}/#organization`,
      name: 'MILLIONS',
      url,
      logo: `${url}/app_icon.svg`,
      description:
        'MILLIONS is a sports streaming and ecommerce platform for athletes, fans, and brands.',
      sameAs: MILLIONS_SOCIAL_PROFILES,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${url}/#website`,
      name: 'MILLIONS',
      url,
      publisher: { '@id': `${url}/#organization` },
    },
  ];
};

const formatDates = (
  scheduleDate: string | null,
  hoursOffset = 2
): { startDate?: string; endDate?: string } => {
  if (!scheduleDate) return {};
  return {
    startDate: dayjs(scheduleDate).toISOString(),
    endDate: dayjs(scheduleDate).add(hoursOffset, 'hours').toISOString(),
  };
};

const generateEventId = (stream: StreamType): string => {
  if (stream.store?.slug && stream.slug) {
    return `${getEnvLink()}/${stream.store.slug}/streams/${stream.slug}`;
  }
  return `${getEnvLink()}/streams`;
};

const generateOffer = (
  price: number | undefined,
  url: string | undefined
): Record<string, unknown> | undefined => {
  if (typeof price === 'undefined' || !url) return undefined;
  return {
    '@type': 'Offer',
    price,
    priceCurrency: DEFAULT_CURRENCY,
    availability: 'https://schema.org/InStock',
    url,
  };
};

const getStoreName = (store: StreamType['store']): string | undefined => {
  if (!store) return undefined;
  return (
    store.storeDetails?.storeName || `${store.firstName} ${store.lastName}`
  );
};

const getPersonSchema = (
  name: string | undefined,
  slug: string | null | undefined
): Record<string, unknown> | undefined => {
  if (!name) return undefined;
  return {
    '@type': 'Person',
    name,
    url: slug ? `${getEnvLink()}/${slug}` : undefined,
  };
};

export const generateVideoMetaDescription = (
  title: string | null | undefined,
  store:
    | {
        firstName?: string | null;
        lastName?: string | null;
        storeDetails?: {
          storeName?: string | null;
          description?: string | null;
          bio?: string | null;
        } | null;
      }
    | null
    | undefined,
  body?: string | null
): string => {
  const MAX = 160;

  // Parse "Subject: Subtitle | ContentType" or "Title | ContentType"
  const parsedTitle = title ?? '';
  const colonIdx = title ? title.indexOf(':') : -1;
  const pipeIdx = title ? title.lastIndexOf(' | ') : -1;

  const subject =
    colonIdx > 0
      ? parsedTitle.slice(0, colonIdx).trim()
      : title?.split(' | ')[0]?.trim();
  const subtitle =
    colonIdx > 0 && pipeIdx > colonIdx
      ? parsedTitle.slice(colonIdx + 1, pipeIdx).trim()
      : null;
  const contentType =
    pipeIdx > 0 ? parsedTitle.slice(pipeIdx + 3).trim() : null;

  const creatorFullName =
    store?.firstName && store?.lastName
      ? `${store.firstName} ${store.lastName}`
      : null;
  const storeName = store?.storeDetails?.storeName;

  // Clean body: strip URLs and "Watch the full stream …" CTAs, first paragraph only
  const cleanBody = cleanDescription(body || '')
    .replace(/https?:\/\/\S+/g, '')
    .replace(/\bWatch the full stream on[^.]*\.?/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  const firstPara = cleanBody.split(/\n+/)[0].trim();

  // Build keyword-rich core from title components
  let core = '';
  if (subject && subtitle && contentType) {
    // e.g. "Brent Reed discusses Nerves of Steel, Business of Boxing in this Press Interview."
    core = `${subject} discusses ${subtitle} in this ${contentType}.`;
  } else if (subject && contentType && creatorFullName) {
    core = `${subject} in a ${contentType} with ${creatorFullName}.`;
  } else if (title) {
    core = pipeIdx > 0 ? title.slice(0, pipeIdx).trim() : title;
  }

  // Append body sentences up to MAX (sentence-boundary aware)
  if (core && firstPara) {
    const sentences = firstPara.match(/[^.!?]+[.!?]+\s*/g) || [];
    let suffix = '';
    for (const s of sentences) {
      if (`${core} ${suffix}${s}`.length <= MAX) suffix += s;
      else break;
    }
    if (suffix) return `${core} ${suffix.trim()}`;
  }

  if (core) return core.length <= MAX ? core : `${core.slice(0, 157)}...`;

  if (firstPara)
    return firstPara.length <= MAX
      ? firstPara
      : `${firstPara.slice(0, 157)}...`;

  const label = creatorFullName || storeName || 'MILLIONS';
  return `Exclusive video by ${label} on MILLIONS.`;
};

export const cleanDescription = (
  description: string | null | undefined
): string => {
  if (!description) return '';
  return description
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&') // Must be last to avoid double-decoding (e.g. &amp;lt; → &lt; not <)
    .replace(/^["'\s]+|["'\s]+$/g, '') // Remove leading/trailing quotes and whitespace
    .trim();
};

export const cleanArticleContent = (
  content: string | null | undefined
): string => {
  const html = parseForInnerHTML(content);
  if (typeof html !== 'string') return '';

  return cleanDescription(
    html.replace(
      /<\/?(?:p|div|h[1-6]|li|ul|ol|blockquote|br|hr|tr|td|th)\b[^>]*>/gi,
      ' '
    )
  )
    .replace(/\s+/g, ' ')
    .trim();
};

const sanitizeForJsonLd = (
  value: string | null | undefined
): string | undefined => {
  if (!value) return undefined;
  return value.replace(/<\//gi, '<\\/');
};

type FaqItem = { question: string; answer: string };

const buildFaqPageSchema = (faq: FaqItem[]): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faq.map((item) => ({
    '@type': 'Question',
    name: sanitizeForJsonLd(item.question),
    acceptedAnswer: {
      '@type': 'Answer',
      text: sanitizeForJsonLd(item.answer),
    },
  })),
});

export const getVideoMimeType = (url: string): string => {
  const path = url.split('?')[0];
  if (path.endsWith('.webm')) return 'video/webm';
  if (path.endsWith('.ogg')) return 'video/ogg';
  if (path.endsWith('.mov')) return 'video/quicktime';
  return 'video/mp4';
};

export const getRoundedVideoDurationSeconds = (
  durationSeconds: number | null | undefined
): number | undefined => {
  const roundedSeconds = Math.round(Number(durationSeconds));

  if (!Number.isFinite(roundedSeconds) || roundedSeconds <= 0) {
    return undefined;
  }

  return roundedSeconds;
};

const getIso8601VideoDuration = (
  durationSeconds: number | null | undefined
): string | undefined => {
  const roundedSeconds = getRoundedVideoDurationSeconds(durationSeconds);

  if (!roundedSeconds) return undefined;

  return dayjs.duration(roundedSeconds, 'seconds').toISOString();
};

const getScheduleDate = (stream: StreamType): string | null => {
  return stream.scheduleDateUtc;
};

const getStreamUrl = (stream: StreamType): string | undefined => {
  return stream.streamStatus === StreamStatus.Ended
    ? getEnvLink() + computeVideoWatchStreamPath(stream)
    : getEnvLink() + computeOrderStreamPath(stream);
};

export const generatePersonStructuredData = (
  store:
    | {
        storeDetails?: {
          storeName?: string | null;
          description?: string | null;
          bio?: string | null;
          avatarURL?: string | null;
        } | null;
        slug?: string | null;
        socialMedia?: { url: string | null }[] | null;
      }
    | null
    | undefined
): Record<string, unknown> | null => {
  if (!store?.storeDetails?.storeName) return null;

  const description = cleanDescription(
    store.storeDetails.description || store.storeDetails.bio || ''
  );
  const sameAs = store.socialMedia
    ?.map((link) => link.url)
    .filter(
      (url): url is string =>
        typeof url === 'string' && /^https?:\/\//.test(url)
    );

  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: store.storeDetails.storeName,
    ...(description && { description }),
    image: store.storeDetails.avatarURL || undefined,
    url: store.slug ? `${getEnvLink()}/${store.slug}` : undefined,
    ...(sameAs?.length && { sameAs }),
  };
};

export const generateProductStructuredData = (
  product:
    | {
        title?: string | null;
        description?: string | null;
        mainImageUrl?: string | null;
        price?: number | null;
        minPrice?: number | null;
        slug?: string | null;
        type?: string | null;
        store?: {
          slug?: string | null;
          storeDetails?: { storeName?: string | null } | null;
        } | null;
        images?: { isMainImage?: boolean | null; url?: string | null }[] | null;
        status?: MemorabiliaStatus | string | null;
      }
    | null
    | undefined,
  productType: 'merch' | 'memorabilia'
): Record<string, unknown> | null => {
  if (!product?.title) return null;

  const url =
    product.store?.slug && product.slug
      ? `${getEnvLink()}/${product.store.slug}/${productType}/${product.slug}`
      : undefined;

  // Merch uses mainImageUrl, memorabilia uses images[].url
  const image =
    product.mainImageUrl ||
    product.images?.find((img) => img.isMainImage)?.url ||
    undefined;

  // Merch has minPrice, memorabilia has price
  const price = product.price ?? product.minPrice;
  const description = cleanDescription(product.description);

  const getAvailability = (): string => {
    const status = product?.status;
    if (!status) return 'https://schema.org/InStock';

    switch (status) {
      case MemorabiliaStatus.Sold:
        return 'https://schema.org/SoldOut';
      case MemorabiliaStatus.Removed:
      case MemorabiliaStatus.Expired:
      case 'Inactive':
        return 'https://schema.org/Discontinued';
      default:
        return 'https://schema.org/InStock';
    }
  };

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: sanitizeForJsonLd(product.title),
    ...(description && { description }),
    ...(image && { image }),
    url,
    ...(product.slug && { sku: product.slug }),
    ...(product.type && { category: product.type }),
    ...(product.store?.storeDetails?.storeName && {
      brand: {
        '@type': 'Brand',
        name: product.store.storeDetails.storeName,
      },
    }),
    ...(typeof price === 'number' && {
      offers: {
        '@type': 'Offer',
        price,
        priceCurrency: DEFAULT_CURRENCY,
        availability: getAvailability(),
        url,
        ...(product.store?.storeDetails?.storeName && {
          seller: {
            '@type': 'Organization',
            name: product.store.storeDetails.storeName,
          },
        }),
        ...(productType === 'merch' && {
          itemCondition: 'https://schema.org/NewCondition',
        }),
      },
    }),
  };
};

export const generateExperienceStructuredData = (
  experience:
    | {
        title?: string | null;
        description?: string | null;
        price?: number | null;
        slug?: string | null;
        store?: {
          slug?: string | null;
          storeDetails?: { storeName?: string | null } | null;
        } | null;
      }
    | null
    | undefined,
  imageUrl?: string
): Record<string, unknown> | null => {
  if (!experience?.title) return null;

  const url =
    experience.store?.slug && experience.slug
      ? `${getEnvLink()}/${experience.store.slug}/experience/${experience.slug}`
      : undefined;
  const description = cleanDescription(experience.description);

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: sanitizeForJsonLd(experience.title),
    ...(description && { description }),
    ...(imageUrl && { image: imageUrl }),
    url,
    ...(experience.store?.storeDetails?.storeName && {
      provider: {
        '@type': 'Person',
        name: experience.store.storeDetails.storeName,
      },
    }),
    ...(typeof experience.price === 'number' && {
      offers: {
        '@type': 'Offer',
        price: experience.price,
        priceCurrency: DEFAULT_CURRENCY,
        availability: 'https://schema.org/InStock',
        url,
      },
    }),
  };
};

export const generateAmaStructuredData = (
  store:
    | {
        slug?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        storeDetails?: {
          storeName?: string | null;
          avatarURL?: string | null;
        } | null;
      }
    | null
    | undefined,
  ama: { price?: number | null } | null | undefined
): Record<string, unknown> | null => {
  if (!store?.slug) return null;

  const providerName =
    store.storeDetails?.storeName ||
    [store.firstName, store.lastName].filter(Boolean).join(' ').trim();
  if (!providerName) return null;

  const url = `${getEnvLink()}/order-personal-videos/${store.slug}`;
  const imageUrl = store.storeDetails?.avatarURL || undefined;
  const profileUrl = `${getEnvLink()}/${store.slug}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: sanitizeForJsonLd(`Personalized video from ${providerName}`),
    serviceType: 'Personalized video message',
    ...(imageUrl && { image: imageUrl }),
    url,
    provider: {
      '@type': 'Person',
      name: sanitizeForJsonLd(providerName),
      ...(imageUrl && { image: imageUrl }),
      url: profileUrl,
    },
    ...(typeof ama?.price === 'number' && {
      offers: {
        '@type': 'Offer',
        price: ama.price,
        priceCurrency: DEFAULT_CURRENCY,
        availability: 'https://schema.org/InStock',
        url,
      },
    }),
  };
};

/**
 * SocialMediaPosting schema for image/text social posts. Video posts emit
 * VideoObject instead (generateVideoStructuredData) — don't double up.
 */
export const generateSocialMediaPostingSchema = (
  post:
    | {
        slug?: string | null;
        title?: string | null;
        body?: string | null;
        createdAt?: string | null;
        image?: { url?: string | null } | null;
        store?: {
          slug?: string | null;
          firstName?: string | null;
          lastName?: string | null;
          storeDetails?: {
            storeName?: string | null;
            avatarURL?: string | null;
          } | null;
        } | null;
      }
    | null
    | undefined
): Record<string, unknown> | null => {
  if (!post?.slug || !post.store?.slug) return null;

  const authorName =
    post.store.storeDetails?.storeName ||
    [post.store.firstName, post.store.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
  if (!authorName) return null;

  const headline = cleanDescription(post.title) || cleanDescription(post.body);
  if (!headline) return null;

  const url = `${getEnvLink()}/${post.store.slug}/social-media-post/${
    post.slug
  }`;
  const profileUrl = `${getEnvLink()}/${post.store.slug}`;
  const imageUrl = post.image?.url || undefined;
  const articleBody = cleanDescription(post.body);
  const datePublished =
    post.createdAt && dayjs(post.createdAt).isValid()
      ? dayjs(post.createdAt).toISOString()
      : undefined;

  return {
    '@context': 'https://schema.org',
    '@type': 'SocialMediaPosting',
    '@id': url,
    url,
    headline: sanitizeForJsonLd(headline),
    ...(articleBody && { articleBody: sanitizeForJsonLd(articleBody) }),
    ...(imageUrl && { image: imageUrl }),
    ...(datePublished && { datePublished }),
    author: {
      '@type': 'Person',
      name: sanitizeForJsonLd(authorName),
      url: profileUrl,
      ...(post.store.storeDetails?.avatarURL && {
        image: post.store.storeDetails.avatarURL,
      }),
    },
    publisher: MILLIONS_ORG,
  };
};

export const generateVideoStructuredData = (
  mediaPost: GetMediaPost_getMediaPost
): Record<string, unknown> | null => {
  if (!('video' in mediaPost)) {
    return null;
  }

  const videoUrl = mediaPost.video.url;
  const thumbnailUrl = mediaPost.thumbnailUrl || mediaPost.video.thumbnailUrl;

  // Google requires contentUrl and thumbnailUrl
  if (!videoUrl || !thumbnailUrl) {
    return null;
  }

  // Strip raw URLs and CTAs before putting in JSON-LD — Google rejects video
  // indexing when description contains bare URLs or "Watch on …" spam text.
  const cleanBody = cleanDescription(mediaPost.body)
    .replace(/https?:\/\/\S+/g, '')
    .replace(/\bWatch the full stream on[^.]*\.?/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  const description = cleanBody || mediaPost.title || 'Video';

  const encodingFormat = getVideoMimeType(videoUrl);
  const duration = getIso8601VideoDuration(
    (mediaPost.video.duration ?? 0) / 1000
  );

  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: sanitizeForJsonLd(mediaPost.title) || 'Video',
    description,
    thumbnailUrl,
    // Must match the sitemap's publication_date, which is
    // COALESCE(publishedAt, createdAt) — see sitemap.service.ts getVideoPosts.
    uploadDate: mediaPost.publishedAt ?? mediaPost.createdAt,
    contentUrl: videoUrl,
    encodingFormat,
    publisher: MILLIONS_ORG,
    ...(duration && { duration }),
  };
};

export const generateItemListStructuredData = (
  name: string,
  items: { url: string }[]
): Record<string, unknown> | null => {
  if (!items.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: sanitizeForJsonLd(name),
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: item.url,
    })),
  };
};

export const generateBroadcastEventStructuredData = (
  stream: StreamType | null | undefined
): Record<string, unknown> | null => {
  if (!stream) return null;

  if (
    stream.streamStatus !== StreamStatus.Scheduled &&
    stream.streamStatus !== StreamStatus.Active
  ) {
    return null;
  }

  const scheduleDate = getScheduleDate(stream);
  const { startDate, endDate } = formatDates(scheduleDate);
  const streamUrl = getStreamUrl(stream);
  const eventId = generateEventId(stream);

  const broadcastEvent: Record<string, unknown> = {
    '@type': 'BroadcastEvent',
    name: `${stream.name || stream.description} - Live Stream`,
    description: `Watch ${stream.name || stream.description} live on MILLIONS`,
    isLiveBroadcast: true,
    startDate,
    endDate,
    url: streamUrl,
    // Google validates a nested subEvent as an Event in its own right, and
    // location is required. Without it every event item is invalid.
    location: {
      '@type': 'VirtualLocation',
      url: streamUrl,
    },
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    broadcastOfEvent: {
      '@id': eventId,
    },
    isAccessibleForFree: Boolean(stream.isFree),
    potentialAction: {
      '@type': 'WatchAction',
      target: getEnvLink() + computeVideoWatchStreamPath(stream),
      name: 'Watch live',
    },
  };

  if (!stream.isFree) {
    const offer = generateOffer(stream.price, streamUrl);
    if (offer) {
      broadcastEvent.offers = offer;
    }
  }

  return broadcastEvent;
};

type TicketEventSchemaFields = Pick<
  PublicTicketEventFields,
  | 'name'
  | 'description'
  | 'coverImageUrl'
  | 'startsAt'
  | 'endsAt'
  | 'status'
  | 'ticketsEnabled'
  | 'salesCloseAt'
  | 'venueName'
  | 'venueAddress'
  | 'ticketPriceFrom'
  | 'host'
  | 'hashtags'
> & {
  matchups?: {
    competitors:
      | {
          name: string | null;
          store: { slug: string | null } | null;
        }[]
      | null;
  }[];
};

/** Everyone on the fight card, profiles or not, once each by name. */
const getTicketEventPerformers = (
  matchups: TicketEventSchemaFields['matchups']
): Record<string, unknown>[] => {
  const seen = new Set<string>();

  return (matchups ?? [])
    .flatMap((matchup) => matchup.competitors ?? [])
    .filter(({ name }) => {
      if (!name || seen.has(name)) {
        return false;
      }
      seen.add(name);
      return true;
    })
    .map(({ name, store }) => ({
      '@type': 'Person',
      name: sanitizeForJsonLd(name as string),
      ...(store?.slug && { url: `${getEnvLink()}/${store.slug}` }),
    }));
};

const TICKET_EVENT_STATUS_SCHEMA: Partial<Record<TicketEventStatus, string>> = {
  [TicketEventStatus.Cancelled]: 'https://schema.org/EventCancelled',
  [TicketEventStatus.Postponed]: 'https://schema.org/EventPostponed',
};

const generateVenuePlace = (
  event: Pick<PublicTicketEventFields, 'venueName' | 'venueAddress'>
): Record<string, unknown> => ({
  '@type': 'Place',
  name: sanitizeForJsonLd(event.venueName),
  address: {
    '@type': 'PostalAddress',
    streetAddress: [
      event.venueAddress.addressLine1,
      event.venueAddress.addressLine2,
    ]
      .filter(Boolean)
      .join(', '),
    addressLocality: event.venueAddress.city,
    addressRegion: event.venueAddress.region,
    postalCode: event.venueAddress.postalCode,
    addressCountry: event.venueAddress.country,
  },
});

/** Tickets from the cheapest tier, only for an event that sells them; in stock while sales are open. */
const generateTicketOffer = (
  event: Pick<
    PublicTicketEventFields,
    'ticketPriceFrom' | 'status' | 'ticketsEnabled' | 'salesCloseAt'
  >,
  url: string
): Record<string, unknown> | undefined =>
  !event.ticketsEnabled || event.ticketPriceFrom == null
    ? undefined
    : {
        '@type': 'AggregateOffer',
        lowPrice: event.ticketPriceFrom,
        priceCurrency: DEFAULT_CURRENCY,
        availability: isTicketEventSelling(event)
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        url,
      };

/** An in-person event selling tickets on its own page. */
export const generateTicketEventStructuredData = (
  event: TicketEventSchemaFields | null | undefined,
  path: string
): Record<string, unknown> | null => {
  if (!event) return null;

  const url = `${getEnvLink()}${path}`;
  const hostName = getStreamerName(event.host);
  const offer = generateTicketOffer(event, url);
  const performers = getTicketEventPerformers(event.matchups);

  return {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    '@id': url,
    url,
    name: sanitizeForJsonLd(event.name),
    description: cleanDescription(event.description),
    ...(event.coverImageUrl && { image: event.coverImageUrl }),
    startDate: dayjs(event.startsAt).toISOString(),
    endDate: dayjs(event.endsAt).toISOString(),
    eventStatus:
      TICKET_EVENT_STATUS_SCHEMA[event.status] ??
      'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: generateVenuePlace(event),
    organizer:
      event.host && hostName
        ? {
            '@type': 'Organization',
            name: sanitizeForJsonLd(hostName),
            url: `${getEnvLink()}/${event.host.slug}`,
          }
        : MILLIONS_ORG,
    ...(performers.length && { performer: performers }),
    ...(event.hashtags.length && { sport: event.hashtags[0].name }),
    ...(offer && { offers: offer }),
  };
};

/** SportsEvent + BreadcrumbList (MILLIONS › Event Tickets › event) for `/events/{slug}`. */
export const generateTicketEventPageStructuredData = (
  event: TicketEventSchemaFields | null | undefined,
  path: string
): Record<string, unknown>[] | null => {
  const sportsEvent = generateTicketEventStructuredData(event, path);

  if (!event || !sportsEvent) return null;

  const baseUrl = getEnvLink();

  return [
    sportsEvent,
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'MILLIONS', item: baseUrl },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Event Tickets',
          item: `${baseUrl}${EVENT_TICKETS}`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: sanitizeForJsonLd(event.name),
          item: `${baseUrl}${path}`,
        },
      ],
    },
  ];
};

export const generateSportsEventStructuredData = (
  stream: StreamType | null | undefined,
  withTicketEvent = false
): Record<string, unknown> | null => {
  if (!stream) return null;

  const scheduleDate = getScheduleDate(stream);
  const { startDate, endDate } = formatDates(scheduleDate);
  const eventId = generateEventId(stream);

  const getEventStatus = (): string => {
    if (stream.streamStatus === StreamStatus.Ended) {
      return 'https://schema.org/EventCompleted';
    }
    if (stream.streamStatus === StreamStatus.Cancelled) {
      return 'https://schema.org/EventCancelled';
    }
    return 'https://schema.org/EventScheduled';
  };

  const sportsEvent: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    '@id': eventId,
    name:
      sanitizeForJsonLd(stream.name) || sanitizeForJsonLd(stream.description),
    description: cleanDescription(stream.description),
    image: stream.imageURL,
    startDate,
    endDate,
    eventStatus: getEventStatus(),
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    // A VirtualLocation with no url is as invalid as no location at all, so
    // fall back to the site rather than emitting an empty one.
    location: {
      '@type': 'VirtualLocation',
      url: stream.store?.slug ? getStreamUrl(stream) : getEnvLink(),
    },
  };

  // Add sport type from hashtags if available
  if (stream.hashtags && stream.hashtags.length > 0) {
    sportsEvent.sport = stream.hashtags[0].name;
  }

  if (stream.store) {
    sportsEvent.organizer = MILLIONS_ORG;

    const performer = getPersonSchema(
      getStoreName(stream.store),
      stream.store.slug
    );
    if (performer) {
      sportsEvent.performer = performer;
    }
  }

  if ('participants' in stream && stream.participants?.length) {
    sportsEvent.competitor = stream.participants.map((participant) => {
      const name =
        participant.storeDetails?.storeName ||
        `${participant.firstName} ${participant.lastName}`;
      return (
        getPersonSchema(name, participant.slug) || {
          '@type': 'Person',
          name,
          url: participant.slug
            ? `${getEnvLink()}/${participant.slug}`
            : undefined,
        }
      );
    });
  }

  // Add sponsors if available
  if (stream.sponsors && stream.sponsors.length > 0) {
    sportsEvent.sponsor = stream.sponsors.map((sponsor) => ({
      '@type': 'Organization',
      name: sponsor.name,
      logo: sponsor.logoUrl,
    }));
  }

  // Only paid, non-cancelled events should emit purchase offers.
  if (
    stream.store?.slug &&
    !stream.isFree &&
    stream.streamStatus !== StreamStatus.Cancelled
  ) {
    const offer = generateOffer(stream.price, getStreamUrl(stream));
    if (offer) {
      sportsEvent.offers = offer;
    }
  }

  // A stream that also sells tickets happens at a venue as well as online.
  if (withTicketEvent && 'ticketEvent' in stream && stream.ticketEvent) {
    const ticketOffer = generateTicketOffer(
      stream.ticketEvent,
      (stream.store?.slug && getStreamUrl(stream)) || getEnvLink()
    );
    sportsEvent.eventAttendanceMode =
      'https://schema.org/MixedEventAttendanceMode';
    sportsEvent.location = [
      generateVenuePlace(stream.ticketEvent),
      sportsEvent.location,
    ];
    if (ticketOffer) {
      sportsEvent.offers = sportsEvent.offers
        ? [sportsEvent.offers, ticketOffer]
        : ticketOffer;
    }
  }

  // Add BroadcastEvent as subEvent for live streams to enable Google's "LIVE" badge
  const broadcastEvent = generateBroadcastEventStructuredData(stream);
  if (broadcastEvent) {
    sportsEvent.subEvent = broadcastEvent;
  }

  return sportsEvent;
};

export const generateVideoObjectSchema = (
  stream: StreamType | null | undefined
): Record<string, unknown> | null => {
  if (!stream) return null;
  const scheduleDate = getScheduleDate(stream);
  const description =
    cleanDescription(stream.description) || stream.name || 'Stream';

  const publisher = {
    '@type': 'Organization',
    name: 'MILLIONS',
    url: getEnvLink(),
  };

  // Don't emit uploadDate as "now" — require an actual date
  const uploadDate = scheduleDate
    ? dayjs(scheduleDate).toISOString()
    : undefined;

  if (
    stream.streamStatus !== StreamStatus.Ended ||
    !hasStreamRecordsV2(stream as GetStream_stream)
  ) {
    return null;
  }

  // Use landscape (desktop) for structured data; no device context in SSR
  const records = getStreamRecordsFromV2(stream as GetStream_stream, false);
  const streamRecord = records[0];
  if (!streamRecord) {
    return null;
  }

  const thumbnailUrl = stream.imageURL || streamRecord.videoThumbnailURL;
  if (!thumbnailUrl) return null;
  const duration = getIso8601VideoDuration(streamRecord.duration);

  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: stream.name || stream.description || 'Stream',
    description,
    thumbnailUrl,
    ...(uploadDate && { uploadDate }),
    contentUrl: streamRecord.videoURL,
    publisher,
    ...(duration && { duration }),
  };
};

/** Trimmed, `#`-less and de-duplicated, in their original order. */
export const getArticleKeywords = (
  keywords: (string | null | undefined)[]
): string[] =>
  keywords
    .map((keyword) => (keyword || '').replace(/^#/, '').trim())
    .filter((keyword, index, all) => keyword && all.indexOf(keyword) === index);

type ArticleSchemaInput =
  | {
      __typename?: string;
      slug?: string | null;
      title?: string | null;
      content?: string | null;
      createdAt?: string | null;
      url?: string | null;
      imageLabel?: string | null;
      updatedAt?: string | null;
      metaDescription?: string | null;
      focusKeyword?: string | null;
      hashtags?: { name: string }[] | null;
      faq?: { question: string; answer: string }[] | null;
      sourceStream?: {
        slug: string;
        store: { slug?: string | null };
      } | null;
      store?: {
        slug?: string | null;
        storeDetails?: { storeName?: string | null } | null;
      } | null;
      sourceInterviewee?: {
        slug?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        storeDetails?: { storeName?: string | null } | null;
      } | null;
    }
  | null
  | undefined;

/**
 * Article + BreadcrumbList for `/{store}/news/{slug}`.
 *
 * `author` is the publishing store, an Organization rather than a Person:
 * our articles are store-published, not bylined. A generated article's store
 * is its host, so there it is a Person. `publisher` stays MILLIONS.
 */
export const generateArticleStructuredData = (
  article: ArticleSchemaInput,
  storeSlug: string | null | undefined
): Record<string, unknown>[] | null => {
  if (!article?.slug || !article.title || !storeSlug) return null;

  const baseUrl = getEnvLink();
  const url = `${baseUrl}/${storeSlug}/news/${article.slug}`;
  const storeName = article.store?.storeDetails?.storeName || 'MILLIONS';
  const articleBody = cleanArticleContent(article.content);
  const datePublished =
    article.createdAt && dayjs(article.createdAt).isValid()
      ? dayjs(article.createdAt).toISOString()
      : undefined;
  const dateModified =
    article.updatedAt && dayjs(article.updatedAt).isValid()
      ? dayjs(article.updatedAt).toISOString()
      : undefined;
  const interviewStoreSlug = article.sourceStream?.store.slug;
  // The store is the host; older articles without sourceInterviewee were owned by the interviewee.
  const interviewee = article.sourceInterviewee?.slug
    ? {
        slug: article.sourceInterviewee.slug,
        name:
          article.sourceInterviewee.storeDetails?.storeName ||
          `${article.sourceInterviewee.firstName || ''} ${
            article.sourceInterviewee.lastName || ''
          }`.trim() ||
          article.sourceInterviewee.slug,
      }
    : { slug: storeSlug, name: storeName };
  const isGenerated = article.__typename === 'GeneratedArticle';
  const keywords = isGenerated
    ? getArticleKeywords([
        article.focusKeyword,
        ...(article.hashtags || []).map((tag) => tag.name),
      ])
    : [];

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': url,
    url,
    headline: sanitizeForJsonLd(article.title),
    ...(article.metaDescription && {
      description: sanitizeForJsonLd(article.metaDescription),
    }),
    ...(articleBody && { articleBody: sanitizeForJsonLd(articleBody) }),
    ...(datePublished && { datePublished }),
    ...(dateModified && { dateModified }),
    ...(article.sourceStream && {
      about: {
        '@type': 'Person',
        name: sanitizeForJsonLd(interviewee.name),
        url: `${baseUrl}/${interviewee.slug}`,
      },
      ...(interviewStoreSlug && {
        isBasedOn: `${baseUrl}/${interviewStoreSlug}/streams/${article.sourceStream.slug}`,
      }),
    }),
    ...(article.url && { image: article.url }),
    ...(keywords.length > 0 && {
      keywords: sanitizeForJsonLd(keywords.join(', ')),
    }),
    author: {
      '@type': isGenerated ? 'Person' : 'Organization',
      name: sanitizeForJsonLd(storeName),
      url: `${baseUrl}/${storeSlug}`,
    },
    publisher: MILLIONS_ORG,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
  };

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'MILLIONS', item: baseUrl },
      {
        '@type': 'ListItem',
        position: 2,
        name: sanitizeForJsonLd(`${storeName} articles`),
        item: `${baseUrl}/${storeSlug}/news`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: sanitizeForJsonLd(article.title),
        item: url,
      },
    ],
  };

  const faq = (article.faq || []).filter(
    (item) => item.question.trim() && item.answer.trim()
  );

  if (!faq.length) {
    return [schema, breadcrumb];
  }

  return [schema, breadcrumb, buildFaqPageSchema(faq)];
};

/**
 * CollectionPage + ItemList + BreadcrumbList for a top-level listing page.
 *
 * `items` may be empty — a listing page is still a CollectionPage worth
 * describing, so only the ItemList is dropped in that case.
 */
export const generateCollectionPageStructuredData = ({
  name,
  description,
  path,
  items = [],
}: {
  name: string;
  description?: string;
  path: string;
  items?: { url: string }[];
}): Record<string, unknown>[] => {
  const baseUrl = getEnvLink();
  const url = `${baseUrl}${path}`;

  const collectionPage: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': url,
    url,
    name: sanitizeForJsonLd(name),
    ...(description && { description: sanitizeForJsonLd(description) }),
    isPartOf: { '@type': 'WebSite', url: baseUrl, name: 'MILLIONS' },
  };

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'MILLIONS', item: baseUrl },
      {
        '@type': 'ListItem',
        position: 2,
        name: sanitizeForJsonLd(name),
        item: url,
      },
    ],
  };

  const itemList = generateItemListStructuredData(name, items);

  return itemList
    ? [collectionPage, breadcrumb, itemList]
    : [collectionPage, breadcrumb];
};

/**
 * WebPage + BreadcrumbList for a marketing landing page, plus FAQPage when
 * the page renders an FAQ.
 */
export const generateLandingPageStructuredData = ({
  name,
  description,
  path,
  faq = [],
}: {
  name: string;
  description: string;
  path: string;
  faq?: FaqItem[];
}): Record<string, unknown>[] => {
  const baseUrl = getEnvLink();
  const url = `${baseUrl}${path}`;

  const page = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': url,
    url,
    name: sanitizeForJsonLd(name),
    description: sanitizeForJsonLd(description),
    isPartOf: { '@type': 'WebSite', url: baseUrl, name: 'MILLIONS' },
    publisher: MILLIONS_ORG,
  };

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'MILLIONS', item: baseUrl },
      {
        '@type': 'ListItem',
        position: 2,
        name: sanitizeForJsonLd(name),
        item: url,
      },
    ],
  };

  return faq.length
    ? [page, breadcrumb, buildFaqPageSchema(faq)]
    : [page, breadcrumb];
};
